/**
 * Builds the MappingPhase labels bundle from a `mediaImport`-namespaced `t()`.
 * Extracted from import-wizard.tsx to keep that file under the ESLint
 * max-lines limit — this is a flat, branch-free object literal, so pulling it
 * into its own pure function doesn't hide any logic.
 */
import type { TFunction } from 'i18next';
import { MediaTypeEnum } from '@ooh/contracts';
import type { MappingPhaseLabels } from '../components/mapping-phase';

// eslint-disable-next-line max-lines-per-function -- flat, branch-free t() object literal; one field per MappingPhaseLabels key, splitting would be arbitrary
export function buildMappingPhaseLabels(t: TFunction<'mediaImport'>): MappingPhaseLabels {
  return {
    nonStandardBanner: t('mapping.nonStandardBanner'),
    columnHeaderSource: t('mapping.columnHeaderSource'),
    columnHeaderTarget: t('mapping.columnHeaderTarget'),
    skipColumnOption: t('mapping.skipColumnOption'),
    fieldSearchPlaceholder: t('mapping.fieldSearchPlaceholder'),
    fieldNoResults: t('mapping.fieldNoResults'),
    groupGeneral: t('mapping.groupGeneral'),
    groupPhysical: t('mapping.groupPhysical'),
    groupMuralFlags: t('mapping.groupMuralFlags'),
    groupDigital: t('mapping.groupDigital'),
    groupInternalCost: t('mapping.groupInternalCost'),
    groupClientPrice: t('mapping.groupClientPrice'),
    supplierLabel: t('mapping.supplierLabel'),
    supplierSelectPlaceholder: t('mapping.supplierSelectPlaceholder'),
    supplierSearchPlaceholder: t('mapping.supplierSearchPlaceholder'),
    supplierNoResults: t('mapping.supplierNoResults'),
    nextButton: t('mapping.nextButton'),
    unmappedRequiredHeading: t('mapping.unmappedRequiredHeading'),
    unmappedRequiredHint: t('mapping.unmappedRequiredHint'),
    missingRequiredHeading: t('mapping.missingRequiredHeading'),
    missingRequiredHint: t('mapping.missingRequiredHint'),
    unknownColumnLabel: t('mapping.unknownColumn'),
    typeOverrideLabel: t('mapping.typeOverrideLabel'),
    typeOverridePlaceholder: t('mapping.typeOverridePlaceholder'),
    typeOverrideAutoNote: t('mapping.typeOverrideAutoNote'),
    editPatternAriaLabel: t('mapping.editPatternAriaLabel'),
    fanOutNoteLabel: t('mapping.fanOutNoteLabel'),
    unmatchedSampleWarningLabel: t('mapping.unmatchedSampleWarningLabel'),
    patternDialogTitle: t('mapping.patternDialogTitle'),
    patternColumnLabelPrefix: t('mapping.patternColumnLabelPrefix'),
    patternSampleValueTemplate: t('mapping.patternSampleValueTemplate'),
    patternFieldLabel: t('mapping.patternFieldLabel'),
    patternAutoDetectedNote: t('mapping.patternAutoDetectedNote'),
    patternPlaceholder: t('mapping.patternPlaceholder'),
    patternUnderstandingLabel: t('mapping.patternUnderstandingLabel'),
    patternUnderstandingInfoLabel: t('mapping.patternUnderstandingInfoLabel'),
    patternUnderstandingInfoText: t('mapping.patternUnderstandingInfoText'),
    patternNoBlanksHint: t('mapping.patternNoBlanksHint'),
    patternMatchedTemplate: t('mapping.patternMatchedTemplate'),
    patternNoMatchText: t('mapping.patternNoMatchText'),
    patternShowMismatchesLabel: t('mapping.patternShowMismatchesLabel'),
    patternHideMismatchesLabel: t('mapping.patternHideMismatchesLabel'),
    patternDoneButtonLabel: t('mapping.patternDoneButtonLabel'),
    recognizedLayoutText: t('mapping.recognizedLayoutText'),
    recognizedLayoutInfoLabel: t('mapping.recognizedLayoutInfoLabel'),
    recognizedLayoutInfoText: t('mapping.recognizedLayoutInfoText'),
    recognizedLayoutClearLabel: t('mapping.recognizedLayoutClearLabel'),
    valueMapDialogTitle: t('mapping.valueMapDialogTitle'),
    valueMapFromColumnLabel: t('mapping.valueMapFromColumnLabel'),
    valueMapToColumnLabel: t('mapping.valueMapToColumnLabel'),
    valueMapUnmappedPlaceholder: t('mapping.valueMapUnmappedPlaceholder'),
    valueMapBlockedWarningTemplate: t('mapping.valueMapBlockedWarningTemplate'),
    constantDialogTitle: t('mapping.constantDialogTitle'),
    constantValueFieldLabel: t('mapping.constantValueFieldLabel'),
    constantEditAriaLabel: t('mapping.constantEditAriaLabel'),
    constantAssignedTemplate: t('mapping.constantAssignedTemplate'),
    sheetPickerLabel: t('mapping.sheetPickerLabel'),
    headerAutoNoteTemplate: t('mapping.headerAutoNoteTemplate'),
    headerAutoInfoLabel: t('mapping.headerAutoInfoLabel'),
    headerAutoInfoText: t('mapping.headerAutoInfoText'),
    fileSummaryTemplate: t('mapping.fileSummaryTemplate'),
    fileSummaryTemplateRowsOnly: t('mapping.fileSummaryTemplateRowsOnly'),
    previewHeading: t('mapping.previewHeading'),
    previewRegionDerivedWarning: t('mapping.previewRegionDerivedWarning'),
    transformModePatternLabel: t('mapping.transformModePatternLabel'),
    transformModeValueMapLabel: t('mapping.transformModeValueMapLabel'),
    modeToggleInfoLabel: t('mapping.modeToggleInfoLabel'),
    modeToggleInfoText: t('mapping.modeToggleInfoText'),
    constantTypeOptionLabels: Object.fromEntries(
      MediaTypeEnum.options.map((mt) => [mt, t(`mapping.constantTypeOptions.${mt}`)]),
    ),
    transformDialogInfoLabel: t('mapping.transformDialogInfoLabel'),
    transformDialogInfoText: t('mapping.transformDialogInfoText'),
    constantValueSearchPlaceholder: t('mapping.constantValueSearchPlaceholder'),
    fieldInfoAriaLabel: t('mapping.fieldInfoAriaLabel'),
    enumOptionsLoadingLabel: t('mapping.enumOptionsLoadingLabel'),
    enumOptionsErrorLabel: t('mapping.enumOptionsErrorLabel'),
  };
}
